import { allowedClockEvents } from "../../../domain/clock";
import type { BreakQualification, ClockEventKind, ClockState } from "../../../domain/clock";
import type { WorkDay } from "../../../domain/workday";

/** A break kind the clock screen offers; the app never starts an unclassified break. */
export type DeclaredBreak = Exclude<BreakQualification, "Unclassified">;

/** A button of the clock screen: an event kind and, for a break start, its declared kind. */
export interface ClockAction {
  readonly kind: ClockEventKind;
  /** `RestBreak` or `Standby` for a break start (ADR-0092), otherwise `null`. */
  readonly breakQualification: DeclaredBreak | null;
}

/** Break kinds offered when starting a break, the statutory rest break first. */
const offeredBreaks: readonly DeclaredBreak[] = ["RestBreak", "Standby"];

/**
 * Returns the actions of the clock screen in the order the UI offers them. A break is always
 * started with a declared kind, so the app never records an unclassified break (T-150).
 *
 * @param state - The clock state.
 * @returns The actions.
 */
export function clockActions(state: ClockState): readonly ClockAction[] {
  const actions = allowedClockEvents(state).flatMap((kind): ClockAction[] =>
    kind === "BreakStart"
      ? offeredBreaks.map((breakQualification) => ({ kind, breakQualification }))
      : [{ kind, breakQualification: null }],
  );

  return actions;
}

/**
 * Returns the declared kind of the break that is running at the end of a day.
 *
 * @param day - The working day.
 * @returns The qualification, or `null` if the last block is not a running break.
 */
export function runningBreakQualification(day: WorkDay): BreakQualification | null {
  const last = day.blocks[day.blocks.length - 1];
  const qualification = last?.kind === "Break" && last.isOpen ? last.breakQualification : null;

  return qualification;
}
